import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import {
    Box,
    Card,
    Typography,
    Button,
    Grid,
    TextField,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import TopSection from "../LT/TopSection";
import { Loader } from "../../components/common/Loader";
import ButtonComponent from "../../components/common/button/Button";
import { addAppAdminTrainingTopicSlice } from "../../redux/actions/addAppAdminTrainingTopicSlice";
import { addAppAdminTrainingContentEndpoint, deleteAppAdminTrainingContentEndpoint, getAppAdminTrainingContentEndpoint, updateAppAdminTrainingContentEndpoint } from "../../common/endpoints";
import { updateAppAdminTrainingTopicSlice } from "../../redux/actions/updateAppAdminTrainingTopicSlice";
import { deleteAppAdminTrainingTopicSlice } from "../../redux/actions/deleteAppAdminTrainingTopicSlice";
import { DeleteTrainingContentModal } from "./DeleteTrainingContentModel";
import { getAppAdminTrainingTopicSlice } from "../../redux/actions/getAppAdminTrainingTopicSlice";

const AdminTraining = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingTopicId, setEditingTopicId] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [saveLoading, setSaveLoading] = useState(false);
    const formRef = useRef(null);
    const dispatch = useDispatch();

    const [topics, setTopics] = useState([]);

    const [formData, setFormData] = useState({
        topicName: "",
        videoTitle: "",
        description: "",
        jobAidLabel: "",
        videoFile: null,
        pdfFile: null,

        videoFileName: "",
        pdfFileName: "",
    });
    const tableLoading = useSelector(
        (state) => state.getAppAdminTrainingTopicSlice?.loading
    );

    useEffect(() => {
        getAllTrainingTopics();
    }, []);

    const getAllTrainingTopics = async () => {
        try {
            const response = await dispatch(
                getAppAdminTrainingTopicSlice({
                    dynamicEndpoint: getAppAdminTrainingContentEndpoint
                })
            );

            if (response?.meta?.requestStatus === "fulfilled") {
                const list = response?.payload || [];

                const formatted = list.map((item) => ({
                    id: item.topicId,
                    topic: item.topicName,
                    video: item.videoTitle,
                    jobAid: item.jobAidLabel,
                    description: item.description,

                    // URLS
                    videoFile: item.azureBlobUrl,
                    pdfFile: item.jobAidUrl,

                    // FILE NAMES
                    videoFileName: item.azureBlobName,
                    pdfFileName: item.jobAidBlobName,
                }));

                setTopics(formatted);
            }
        } catch (error) {
            console.error("GET TOPICS ERROR", error);
        }
    };

    const handleSaveTopic = async () => {
        if (saveLoading) return;

        setSaveLoading(true);

        let response;

        try {
            const payload = {
                topicName: formData.topicName,
                description: formData.description,
                jobAidLable: formData.jobAidLabel,
                role: "Admin",
                videoTitle: formData.videoTitle,
            };

            const formPayload = new FormData();

            if (formData.videoFile) {
                formPayload.append(
                    "file",
                    formData.videoFile
                );
            }

            if (formData.pdfFile) {
                formPayload.append(
                    "jobAid",
                    formData.pdfFile
                );
            }

            formPayload.append(
                "data",
                new Blob(
                    [JSON.stringify(payload)],
                    {
                        type: "application/json",
                    }
                )
            );

            // UPDATE API
            if (editingTopicId) {
                response = await dispatch(
                    updateAppAdminTrainingTopicSlice({
                        dynamicEndpoint:
                            `${updateAppAdminTrainingContentEndpoint}/${editingTopicId}`,
                        data: formPayload,
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    })
                );
            }

            // ADD API
            else {
                response = await dispatch(
                    addAppAdminTrainingTopicSlice({
                        dynamicEndpoint:
                            addAppAdminTrainingContentEndpoint,
                        data: formPayload,
                        headers: {
                            "Content-Type":
                                "multipart/form-data",
                        },
                    })
                );
            }

            if (
                response?.meta?.requestStatus ===
                "fulfilled"
            ) {
                toast.success(
                    editingTopicId
                        ? "Training Content Topic Updated Successfully"
                        : "Training Content Topic Added Successfully",
                    {
                        autoClose: 2000,
                    }
                );

                getAllTrainingTopics();

                setFormData({
                    topicName: "",
                    videoTitle: "",
                    description: "",
                    jobAidLabel: "",
                    videoFile: null,
                    pdfFile: null,
                });

                setEditingIndex(null);
                setEditingTopicId(null);
                setShowAddForm(false);
            }

            else {
                const errorMessage =
                    response?.payload?.technicalErrorMessage ||
                    response?.payload?.errorMessage ||
                    "Something went wrong";

                toast.error(errorMessage, {
                    autoClose: 3000,
                });
            }

        } catch (error) {
            console.error(error);

            const errorMessage =
                error?.response?.data?.technicalErrorMessage ||
                error?.response?.data?.errorMessage ||
                "Something went wrong";

            toast.error(errorMessage, {
                autoClose: 3000,
            });
        }

        finally {
            setSaveLoading(false);
        }
    };

    const handleEdit = (index) => {
        const item = topics[index];

        setEditingTopicId(item.id);

        setFormData({
            topicName: item.topic,
            jobAidLabel: item.jobAid,
            videoTitle: item.video,
            description: item.description || "",

            videoFile: item.videoFile || null,
            pdfFile: item.pdfFile || null,

            videoFileName: item.videoFileName || "",
            pdfFileName: item.pdfFileName || "",
        });

        setEditingIndex(index);
        setTimeout(() => {
            formRef.current?.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }, 100);
        setShowAddForm(true);
    };

    const handleDeleteClick = (topic) => {
        setSelectedTopic(topic);
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
        setSelectedTopic(null);
    };

    const handleDeleteConfirmation = async () => {
        try {
            const response = await dispatch(
                deleteAppAdminTrainingTopicSlice({
                    dynamicEndpoint: `${deleteAppAdminTrainingContentEndpoint}/${selectedTopic.id}`,
                })
            );

            if (response?.meta?.requestStatus === "fulfilled") {
                toast.success("Topic deleted successfully", {
                    autoClose: 2000,
                });
                getAllTrainingTopics();
            }

            handleCloseDeleteModal();

        } catch (error) {
            console.error(error);
            toast.error("Failed to delete topic", {
                autoClose: 2000,
            });
        }
    };

    const handleVideoUpload = (e) => {
        const file = e.target.files[0];

        if (file) {

            // TYPE VALIDATION
            if (file.type !== "video/mp4") {
                toast.error("Only MP4 videos are allowed");
                return;
            }

            // SIZE VALIDATION (1GB)
            const maxSize = 1024 * 1024 * 1024;

            if (file.size > maxSize) {
                toast.error("Video size should not exceed 1GB");
                return;
            }

            setFormData({
                ...formData,
                videoFile: file,
            });
        }
    };

    const handlePdfUpload = (e) => {
        const file = e.target.files[0];

        if (file) {

            // TYPE VALIDATION
            // if (file.type !== "application/pdf") {
            //     toast.error("Only PDF files are allowed");
            //     return;
            // }

            // SIZE VALIDATION (1GB)
            const maxSize = 1024 * 1024 * 1024;

            if (file.size > maxSize) {
                toast.error("PDF size should not exceed 1GB");
                return;
            }

            setFormData({
                ...formData,
                pdfFile: file,
            });
        }
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    return (
        <Box sx={{ p: 2 }}>
            <TopSection
                labelList={[
                    { title: "Home", path: "/dashboard" },
                    { title: "Admin Training Content" },
                ]}
                headerName="Admin Training Content"
                hideHeader
            />

            {/* HEADER ROW */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: 2,
                    mb: 2,
                }}
            >
                <Typography variant="h6">Topics</Typography>
                <ButtonComponent
                    type="secondary"
                    maxWidth="200px"
                    label="Add Topic"
                    height="40px"
                    onClick={() => {
                        setShowAddForm(true);
                        setTimeout(() => {
                            formRef.current?.scrollIntoView({
                                behavior: "smooth",
                                block: "start",
                            });
                        }, 100);
                    }}
                />

            </Box>

            {/* EXISTING TOPICS (ALWAYS VISIBLE) */}
            {tableLoading ? <Loader /> : <Card sx={{ p: 2, mb: 3 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                    Existing Topics
                </Typography>

                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><b>Topic</b></TableCell>
                            <TableCell><b>Video Title</b></TableCell>
                            <TableCell><b>Job Aid</b></TableCell>
                            <TableCell><b>Action</b></TableCell>
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {topics.map((t, i) => (
                            <TableRow key={i}>
                                <TableCell>{t.topic}</TableCell>
                                <TableCell>{t.video}</TableCell>
                                <TableCell>{t.jobAid}</TableCell>

                                {/* ACTION COLUMN */}
                                <TableCell>
                                    <IconButton size="small" onClick={() => handleEdit(i)} color="primary" fontSize="small">
                                        <EditIcon />
                                    </IconButton>

                                    <IconButton size="small" onClick={() => handleDeleteClick(t)} color="error" fontSize="small">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>

                </Table>
            </Card>}

            {/* NEW TOPIC FORM (ONLY WHEN BUTTON CLICKED) */}
            {showAddForm && (
                <Card sx={{ p: 3 }} ref={formRef}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Add Topic
                    </Typography>

                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Topic Name"
                                name="topicName"
                                value={formData.topicName}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                label="Video Title"
                                name="videoTitle"
                                value={formData.videoTitle}
                                onChange={handleChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Job Aid Label"
                                name="jobAidLabel"
                                value={formData.jobAidLabel}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Description"
                                name="description"
                                multiline
                                rows={2}
                                value={formData.description}
                                onChange={handleChange}
                            />
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{ width: "100%" }}>
                                <Typography variant="body2" fontWeight={500} mb={0.5}>
                                    Upload MP4 Video (Maximum Size : 1GB)
                                </Typography>

                                <TextField
                                    fullWidth
                                    size="small"
                                    type="file"
                                    inputProps={{
                                        accept: "video/mp4"
                                    }}
                                    onChange={handleVideoUpload}
                                />

                                {formData.videoFile && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "green",
                                            mt: 1,
                                            display: "block"
                                        }}
                                    >
                                        Selected Video: {
                                            typeof formData.videoFile === "string"
                                                ? formData.videoFileName
                                                : formData.videoFile.name
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Box sx={{ width: "100%" }}>
                                <Typography variant="body2" fontWeight={500} mb={0.5}>
                                    Upload PDF Document
                                </Typography>

                                <TextField
                                    fullWidth
                                    size="small"
                                    type="file"
                                    inputProps={{
                                        accept: ".pdf"
                                    }}
                                    onChange={handlePdfUpload}
                                />

                                {formData.pdfFile && (
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "green",
                                            mt: 1,
                                            display: "block"
                                        }}
                                    >
                                        Selected PDF: {
                                            typeof formData.pdfFile === "string"
                                                ? formData.pdfFileName
                                                : formData.pdfFile.name
                                        }
                                    </Typography>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                            <Button
                                onClick={() => {
                                    setShowAddForm(false);
                                    setEditingIndex(null);
                                    setFormData({
                                        topicName: "",
                                        videoTitle: "",
                                        videoUrl: "",
                                        description: "",
                                        jobAid: "",
                                        jobAidLabel: "",
                                    });
                                }}
                            >
                                Cancel
                            </Button>

                            <ButtonComponent
                                type="secondary"
                                maxWidth="200px"
                                height="40px"
                                label={editingIndex !== null ? "Update" : "Save"}
                                disabled={saveLoading}
                                onClick={handleSaveTopic}
                            >
                            </ButtonComponent>

                        </Grid>
                    </Grid>
                </Card>
            )}
            <DeleteTrainingContentModal
                openDeleteConfirmationModalData={
                    openDeleteModal
                }
                handleCloseDeleteModal={
                    handleCloseDeleteModal
                }
                handleDeleteConfirmation={
                    handleDeleteConfirmation
                }
                selectedTopic={selectedTopic}
            />
        </Box>
    );
};

export default AdminTraining;
